import { useState } from 'react'
import { Link } from 'react-router'

import { ApiError } from '@/api/client'
import { usePaymentMethods, usePayInvoice } from '@/api/queries'
import type { Invoice } from '@/api/types'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { brandLabel, cardLabel, expiryLabel, isExpired } from '@/lib/cards'
import { centsLabel } from '@/lib/billing'
import { invoiceLabel, payErrorText, preferredCard } from '@/lib/invoices'

/** "Pay now": choose a card, pay the open invoice, show Stripe's decline sentence verbatim. */
export function PayInvoiceDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const cards = usePaymentMethods()
  const pay = usePayInvoice()
  const [choice, setChoice] = useState<string | undefined>(undefined)
  const [paid, setPaid] = useState(false)
  const list = cards.data ?? []
  const selected = choice ?? preferredCard(list)

  const error = pay.error
  const code = error instanceof ApiError ? error.code : undefined
  const message = error instanceof Error ? error.message : ''

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pay.isPending) onClose()
      }}
    >
      <DialogContent data-testid="pay-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Pay invoice {invoiceLabel(invoice)}</DialogTitle>
          <DialogDescription>
            {centsLabel(invoice.amount_due_cents)} is due. Choose the card to charge.
          </DialogDescription>
        </DialogHeader>
        {paid ? (
          <Alert variant="info" data-testid="pay-success">
            <AlertDescription>Paid. A receipt is on its way to your billing contacts.</AlertDescription>
          </Alert>
        ) : list.length === 0 && cards.isSuccess ? (
          <Alert variant="warning">
            <AlertDescription>
              You have no card on file. Add one on the{' '}
              <Link to="/app/plan" className="font-medium underline">
                Plan page
              </Link>
              , then come back.
            </AlertDescription>
          </Alert>
        ) : (
          <fieldset className="grid gap-2" aria-label="Card">
            {list.map((card) => (
              <label
                key={card.id}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2 text-sm has-[:checked]:border-primary"
              >
                <input
                  type="radio"
                  name="pay-card"
                  className="accent-primary"
                  checked={selected === card.id}
                  onChange={() => {
                    setChoice(card.id)
                  }}
                />
                <span className="font-medium">{cardLabel(card)}</span>
                <span className="text-muted-foreground">
                  {brandLabel(card.brand)} · expires {expiryLabel(card)}
                  {isExpired(card) ? ' (expired)' : ''}
                  {card.is_default ? ' · default' : ''}
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {pay.isError ? (
          <Alert variant="destructive" data-testid="pay-error">
            <AlertDescription>{payErrorText(code, message)}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={pay.isPending}>
            {paid ? 'Close' : 'Cancel'}
          </Button>
          {paid ? null : (
            <Button
              type="button"
              data-testid="pay-confirm"
              disabled={pay.isPending || selected === undefined}
              onClick={() => {
                pay.mutate(
                  { invoiceId: invoice.id, ...(selected === undefined ? {} : { paymentMethod: selected }) },
                  {
                    onSuccess: () => {
                      setPaid(true)
                    },
                  },
                )
              }}
            >
              {pay.isPending ? 'Paying…' : `Pay ${centsLabel(invoice.amount_due_cents)}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
